import { PageLink, PageTitle } from "_metronic/layout/core";
import { ExternalAccessData } from "app/modules/auth";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "setup";
import { CPLAnalytics } from "setup/appInsights/CPLAnalytics";
import { externalPasswordChange, selectActionSuccessful, selectLoading } from "../ExternalAccessSlice";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { Form, Formik } from "formik";
import { selectUpdatingPassword } from "app/modules/admin/userProfile/components/PasswordChangeSlice";
import { passwordValidationRegex } from "cpl-react-common/components/Constants";
import * as Yup from "yup";
import { useEffect } from "react";
import { ExternalPasswordChangeModel } from "services/api/cplauthapi/CPLAuthAPI";
import CPLPasswordInput from "cpl-react-common/components/Inputs/CPLPasswordInput";

const externalAccessPasswordChangeBreadCrumbs: Array<PageLink> = [
  {
    title: "Password",
    path: "/passwordchange",
    isSeparator: false,
    isActive: true,
  },
];

const passwordChangeSchema = Yup.object().shape({
  password: Yup.string()
    .required("Please enter your new password")
    .matches(
      passwordValidationRegex,
      "Password must be at least 8 characters in length and contain one number, one upper case letter and one lower case letter"
    ),
  repeatPassword: Yup.string()
    .required("Please re-enter your password")
    .oneOf([Yup.ref("password")], "Password does not match"),
});

const ExternalAccessPasswordChangePage = () => {
  const externalAccessData = useSelector<RootState, ExternalAccessData | undefined>(
    ({ auth }) => auth.externalAccess,
    shallowEqual
  );
  const loading = useSelector(selectLoading);
  const thunkDispatch = useThunkDispatch();

  const updatingPassword = useSelector(selectUpdatingPassword);

  const actionSuccessful = useSelector(selectActionSuccessful);

  useEffect(() => {
    if (actionSuccessful) {
      window.location.href = "/dashboard";
    }
  }, [actionSuccessful]);

  const initialValues = {
    password: "",
    repeatPassword: "",
  };

  const handleUpdatePassword = (newPassword: string) => {
    thunkDispatch(
      externalPasswordChange({
        passwordModel: new ExternalPasswordChangeModel({
          singleUseToken: externalAccessData?.singleUseToken,
          newPassword,
        }),
      })
    );
  };

  return (
    <CPLAnalytics pageName="ExternalAccessPasswordChange">
      <PageTitle breadcrumbs={externalAccessPasswordChangeBreadCrumbs} />
      <ActivityIndicator isBusy={loading}>
        <div className="card mb-5 mb-xl-10">
          <div className="card-header">
            <div className="card-title m-0">
              <h3 className="fw-bolder m-0">Change Password</h3>
            </div>
          </div>
          <div className="card-body border-top p-9">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              onSubmit={async (values) => {
                handleUpdatePassword(values.password);
              }}
              validationSchema={passwordChangeSchema}
            >
              {({ getFieldProps }) => (
                <Form>
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">Password</label>
                    <div className="col-lg-8">
                      <div className="row">
                        <div className="col-lg-12 fv-row">
                          <CPLPasswordInput
                            data-test-id="external-access-password-change-form__password-input"
                            id="password"
                            getFieldProps={getFieldProps}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-6">
                    <label className="col-lg-4 col-form-label required fw-bold fs-6">Re-enter Password</label>
                    <div className="col-lg-8">
                      <div className="row">
                        <div className="col-lg-12 fv-row">
                          <CPLPasswordInput
                            data-test-id="external-access-password-change-form__repeat-password-input"
                            id="repeatPassword"
                            getFieldProps={getFieldProps}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    data-test-id="external-access-password-change-form__submit-button"
                    type="submit"
                    className="btn btn-primary"
                    disabled={updatingPassword}
                  >
                    {!updatingPassword && "Save Changes"}
                    {updatingPassword && (
                      <span className="indicator-progress" style={{ display: "block" }}>
                        Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </ActivityIndicator>
    </CPLAnalytics>
  );
};

export default ExternalAccessPasswordChangePage;
